export { getBusinessWeekStartDayIndex } from "../../../utilities/businessWeek";

export const normalizeDate = (date) => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

const getStartOfBusinessWeek = (referenceDate, businessWeekStartDayIndex = 1) => {
  const date = normalizeDate(referenceDate);
  const diff = (date.getDay() - businessWeekStartDayIndex + 7) % 7;
  date.setDate(date.getDate() - diff);
  return date;
};

const getEndOfBusinessWeek = (startOfWeek) => {
  const end = normalizeDate(startOfWeek);
  end.setDate(end.getDate() + 6);
  return end;
};

const getStartOfMonth = (date) =>
  normalizeDate(new Date(date.getFullYear(), date.getMonth(), 1));

const getEndOfMonth = (date) =>
  normalizeDate(new Date(date.getFullYear(), date.getMonth() + 1, 0));

const getQuarterStart = (date) => {
  const quarter = Math.floor(date.getMonth() / 3);
  return normalizeDate(new Date(date.getFullYear(), quarter * 3, 1));
};

const getQuarterEnd = (date) => {
  const quarter = Math.floor(date.getMonth() / 3);
  return normalizeDate(new Date(date.getFullYear(), quarter * 3 + 3, 0));
};

export const DATE_RANGE_OPTION_LABELS = {
  today: "Today",
  yesterday: "Yesterday",
  last_3_days: "Last 3 Days",
  last_week: "Last Completed Week",
  last_two_weeks: "Last 2 Completed Weeks",
  current_week_to_date: "Current Week to Date",
  last_month: "Last Completed Month",
  current_month_to_date: "Current Month to Date",
  last_3_months: "Last 3 Completed Months",
  last_6_months: "Last 6 Months (Rolling)",
  last_completed_quarter: "Last Completed Quarter",
  current_quarter_to_date: "Current Quarter to Date",
  last_year: "Last 12 Months (Rolling)",
  current_year_to_date: "Current Year to Date",
  last_5_years: "Last 5 Years (Rolling)",
  next_week: "Next Week",
  next_month: "Next Month",
  next_quarter: "Next Quarter",
  custom: "Enter Custom Date Range",
};

export const DATE_RANGE_GROUPS = [
  {
    label: "Custom",
    options: [{ value: "custom", label: DATE_RANGE_OPTION_LABELS.custom }],
  },
  {
    label: "Current Periods",
    options: [
      { value: "today", label: DATE_RANGE_OPTION_LABELS.today },
      {
        value: "current_week_to_date",
        label: DATE_RANGE_OPTION_LABELS.current_week_to_date,
      },
      {
        value: "current_month_to_date",
        label: DATE_RANGE_OPTION_LABELS.current_month_to_date,
      },
      {
        value: "current_quarter_to_date",
        label: DATE_RANGE_OPTION_LABELS.current_quarter_to_date,
      },
      {
        value: "current_year_to_date",
        label: DATE_RANGE_OPTION_LABELS.current_year_to_date,
      },
    ],
  },
  {
    label: "Recently Completed Periods",
    options: [
      { value: "yesterday", label: DATE_RANGE_OPTION_LABELS.yesterday },
      { value: "last_3_days", label: DATE_RANGE_OPTION_LABELS.last_3_days },
      { value: "last_week", label: DATE_RANGE_OPTION_LABELS.last_week },
      {
        value: "last_two_weeks",
        label: DATE_RANGE_OPTION_LABELS.last_two_weeks,
      },
      { value: "last_month", label: DATE_RANGE_OPTION_LABELS.last_month },
      {
        value: "last_3_months",
        label: DATE_RANGE_OPTION_LABELS.last_3_months,
      },
      {
        value: "last_completed_quarter",
        label: DATE_RANGE_OPTION_LABELS.last_completed_quarter,
      },
    ],
  },
  {
    label: "Extended History",
    options: [
      {
        value: "last_6_months",
        label: DATE_RANGE_OPTION_LABELS.last_6_months,
      },
      { value: "last_year", label: DATE_RANGE_OPTION_LABELS.last_year },
      { value: "last_5_years", label: DATE_RANGE_OPTION_LABELS.last_5_years },
    ],
  },
  {
    label: "Upcoming Periods",
    options: [
      { value: "next_week", label: DATE_RANGE_OPTION_LABELS.next_week },
      { value: "next_month", label: DATE_RANGE_OPTION_LABELS.next_month },
      { value: "next_quarter", label: DATE_RANGE_OPTION_LABELS.next_quarter },
    ],
  },
];

export const getDateRangeDisplayLabel = (value) => {
  if (value === "custom") {
    return "Custom Range";
  }
  return DATE_RANGE_OPTION_LABELS[value] || "Custom Range";
};

export const getPresetDateRange = ({
  preset,
  today = new Date(),
  businessWeekStartDayIndex = 1,
} = {}) => {
  const normalizedToday = normalizeDate(today);
  const yesterday = new Date(normalizedToday);
  yesterday.setDate(yesterday.getDate() - 1);

  switch (preset) {
    case "today":
      return {
        startDate: new Date(normalizedToday),
        endDate: new Date(normalizedToday),
      };
    case "yesterday": {
      const start = new Date(normalizedToday);
      start.setDate(start.getDate() - 1);
      return { startDate: start, endDate: new Date(yesterday) };
    }
    case "last_3_days": {
      const start = new Date(normalizedToday);
      start.setDate(start.getDate() - 3);
      return { startDate: start, endDate: new Date(yesterday) };
    }
    case "last_week": {
      const currentWeekStart = getStartOfBusinessWeek(
        normalizedToday,
        businessWeekStartDayIndex
      );
      const start = new Date(currentWeekStart);
      start.setDate(start.getDate() - 7);
      const end = new Date(currentWeekStart);
      end.setDate(end.getDate() - 1);
      return { startDate: start, endDate: end };
    }
    case "last_two_weeks": {
      const currentWeekStart = getStartOfBusinessWeek(
        normalizedToday,
        businessWeekStartDayIndex
      );
      const start = new Date(currentWeekStart);
      start.setDate(start.getDate() - 14);
      const end = new Date(currentWeekStart);
      end.setDate(end.getDate() - 1);
      return { startDate: start, endDate: end };
    }
    case "current_week_to_date": {
      const start = getStartOfBusinessWeek(
        normalizedToday,
        businessWeekStartDayIndex
      );
      return { startDate: start, endDate: new Date(normalizedToday) };
    }
    case "last_month": {
      const lastMonthReference = new Date(
        normalizedToday.getFullYear(),
        normalizedToday.getMonth() - 1,
        1
      );
      return {
        startDate: getStartOfMonth(lastMonthReference),
        endDate: getEndOfMonth(lastMonthReference),
      };
    }
    case "current_month_to_date":
      return {
        startDate: getStartOfMonth(normalizedToday),
        endDate: new Date(normalizedToday),
      };
    case "last_3_months": {
      const startReference = new Date(
        normalizedToday.getFullYear(),
        normalizedToday.getMonth() - 3,
        1
      );
      const periodStart = getStartOfMonth(startReference);
      const periodEnd = getEndOfMonth(
        new Date(normalizedToday.getFullYear(), normalizedToday.getMonth() - 1, 1)
      );
      return { startDate: periodStart, endDate: periodEnd };
    }
    case "last_6_months": {
      const start = new Date(normalizedToday);
      start.setDate(start.getDate() - 183);
      return { startDate: start, endDate: new Date(yesterday) };
    }
    case "last_completed_quarter": {
      const lastQuarterReference = new Date(
        normalizedToday.getFullYear(),
        normalizedToday.getMonth() - 3,
        1
      );
      return {
        startDate: getQuarterStart(lastQuarterReference),
        endDate: getQuarterEnd(lastQuarterReference),
      };
    }
    case "current_quarter_to_date":
      return {
        startDate: getQuarterStart(normalizedToday),
        endDate: new Date(normalizedToday),
      };
    case "last_year": {
      const start = new Date(normalizedToday);
      start.setDate(start.getDate() - 365);
      return { startDate: start, endDate: new Date(yesterday) };
    }
    case "current_year_to_date": {
      const yearStart = normalizeDate(
        new Date(normalizedToday.getFullYear(), 0, 1)
      );
      return { startDate: yearStart, endDate: new Date(normalizedToday) };
    }
    case "last_5_years": {
      const start = new Date(normalizedToday);
      start.setDate(start.getDate() - 1825);
      return { startDate: start, endDate: new Date(yesterday) };
    }
    case "next_week": {
      const nextWeekStart = getStartOfBusinessWeek(
        normalizedToday,
        businessWeekStartDayIndex
      );
      nextWeekStart.setDate(nextWeekStart.getDate() + 7);
      const nextWeekEnd = getEndOfBusinessWeek(nextWeekStart);
      return { startDate: nextWeekStart, endDate: nextWeekEnd };
    }
    case "next_month": {
      const nextMonthDate = new Date(
        normalizedToday.getFullYear(),
        normalizedToday.getMonth() + 1,
        1
      );
      return {
        startDate: getStartOfMonth(nextMonthDate),
        endDate: getEndOfMonth(nextMonthDate),
      };
    }
    case "next_quarter": {
      const nextQuarterReference = new Date(
        normalizedToday.getFullYear(),
        normalizedToday.getMonth() + 3,
        1
      );
      return {
        startDate: getQuarterStart(nextQuarterReference),
        endDate: getQuarterEnd(nextQuarterReference),
      };
    }
    case "custom":
      return {
        startDate: null,
        endDate: null,
        requiresCustomInput: true,
      };
    default:
      return {
        startDate: null,
        endDate: null,
        requiresCustomInput: false,
      };
  }
};

export const getAllDateRangeOptions = () =>
  DATE_RANGE_GROUPS.flatMap((group) => group.options);
